/**
 *  Version page configuration item
 *  Author: Teemo
 *  CreatedAt: 2021-03-08
 */

import { EditIcon } from '@/src/components/EditableTable/EditableTable';
import GlobalTooltip from '@/src/components/GlobalTooltip/GlobalTooltip';
// import Link from '@/src/components/Link/Link';
import SalesChannelStatusIcon from '@/src/layout/Components/SalesChannel/SalesChannelStatusIcon';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button } from 'antd';
import moment from 'moment';
import React from 'react';
import { DropDown, Menu, MenuItem } from 'src/components/Antd';
import { TableInput } from 'src/components/EditableTable/TableForm/TableForm';
import {
  handleOnFormatCurrentSCTimeAndDate,
  handleOnFormatCurrentSCTimeAndDateWithTimezone,
} from 'src/tools/tools';
import css from './css.less';

enum ActionType {
  Button = 'Button',
  Link = 'Link',
}

//Breadcrumb configuration
const breadcrumbData = (name, { getI18n }) => [
  {
    url: '',
    name: getI18n('general.sider.home'),
  },
  {
    url: '/salesChannels',
    name: getI18n('general.sider.salesChannel'),
  },
  {
    url: '',
    name: name,
  },
  {
    url: '',
    name: getI18n('general.sider.content'),
  },
];

const columnsNoDraft = (
  tableOnChange,
  tableOnBlur,
  duplicateFun,
  MultiErrorModalFun,
  scheduleFun,
  editFun,
  saleChannelUuid,
  timezoneGmt,
  canView,
  canEdit,
  canPublish,
  canDelete,
  { getI18n },
) => [
  {
    title: getI18n('general.status'),
    dataIndex: 'status',
    default: true,
    optional: true,
    className: css.columnClass,
    childGenerate: (tags) => <SalesChannelStatusIcon status={tags} />,
    key: 'status',
    width: 88,
  },
  {
    title: () => (
      <div>
        <EditIcon />
        <span>{getI18n('salesChannelContent.index.versionname')}</span>
      </div>
    ),
    key: 'versionName',
    dataIndex: 'versionName',
    childGenerate: (text, record, index) => {
      const { notesError, defaultVersion, publishable } = record;
      return (
        <div className={css.draftName}>
          <div className={css.draftNameContain}>
            {defaultVersion && (
              <span className={css.defaultName}>
                {getI18n('salesChannelContent.index.default')}
              </span>
            )}
            <TableInput
              maxLength={100}
              disabled={!canEdit}
              containerClassName={defaultVersion ? css.containerClassName : ''}
              error={notesError}
              defaultValue={text}
              onBlurCallback={tableOnBlur}
              onChange={(e) => {
                tableOnChange(index, { name: e }, record.uuid);
              }}
            />
          </div>
          {!publishable && (
            <ExclamationCircleFilled className={css.draftNameErr} rev />
          )}
        </div>
      );
    },
    minWidth: 264,
    default: true,
  },
  {
    title: `${getI18n(
      'salesChannelContent.index.publishTime',
    )} (UTC ${timezoneGmt})`,
    key: 'beginTime',
    dataIndex: 'beginTime',
    minWidth: 180,
    default: true,
    align: 'center',
    childGenerate: (text, record, index) => {
      const { beginDate, beginTime } = record;
      return text ? (
        <GlobalTooltip
          title={moment(`${beginDate} ${beginTime}`).format(
            'DD MMM YYYY  HH:mm',
          )}>
          <div className={css.tableTime}>
            {handleOnFormatCurrentSCTimeAndDate(`${beginDate} ${beginTime}`)}
          </div>
        </GlobalTooltip>
      ) : (
        <div className={css.tableTime}>-</div>
      );
    },
  },
  {
    title: `${getI18n('general.endTime')} (UTC ${timezoneGmt})`,
    key: 'endTime',
    dataIndex: 'endTime',
    minWidth: 180,
    default: true,
    align: 'center',
    childGenerate: (text, record, index) => {
      const { endDate, endTime } = record;
      return text ? (
        <GlobalTooltip
          title={handleOnFormatCurrentSCTimeAndDate(`${endDate} ${endTime}`)}>
          <div className={css.tableTime}>
            {handleOnFormatCurrentSCTimeAndDate(`${endDate} ${endTime}`)}
          </div>
        </GlobalTooltip>
      ) : (
        <div className={css.tableTime}>-</div>
      );
    },
  },
  {
    title: getI18n(
      'general.advancedFilters.commonStoreProductsWithTable.actions',
    ),
    key: 'endTime',
    dataIndex: 'endTime',
    minWidth: 378,
    default: true,
    align: 'center',
    childGenerate: (text, record, index) => {
      const { versionName = '', uuid = '', status, defaultVersion } = record;
      return (
        <div className={css.menuContain}>
          {overlayActionNoDraft({
            duplicateFun: () => duplicateFun(versionName, uuid),
            MultiErrorModalFun: (fun) =>
              MultiErrorModalFun(uuid, status, fun, defaultVersion),
            scheduleFun: () =>
              scheduleFun(
                uuid,
                getI18n('salesChannelContent.index.schedule'),
                status,
              ),
            editFun: () => editFun(uuid),
            editLink: `/salesChannel/editor/content/homepage/${saleChannelUuid}/${uuid}`,
            canView,
            canEdit,
            canPublish,
            canDelete,
            getI18n,
          }).map((item) =>
            item.type === ActionType.Button ? (
              <DropDown.Button
                key={item.key}
                getPopupContainer={() => document.body}
                onClick={() => {
                  if (!item.disabled) {
                    item.function();
                  }
                }}
                overlay={overlay(item.data)}
                placement='bottomRight'
                trigger={['click']}>
                {item.name}
              </DropDown.Button>
            ) : (
              <></>
              // <Link
              //   className={css.link}
              //   to={item.to}
              //   key={item.name}
              //   disable={item.disabled}>
              //   {item.name}
              // </Link>
            ),
          )}
        </div>
      );
    },
  },
];

const columnsDraft = (
  tableOnChange,
  tableOnBlur,
  duplicateFun,
  MultiErrorModalFun,
  publishFun,
  editFun,
  saleChannelUuid,
  timezoneGmt,
  canView,
  canEdit,
  canPublish,
  canDelete,
  isPlanActiveOrPending,
  { getI18n },
) => [
  {
    title: getI18n('general.status'),
    dataIndex: 'status',
    default: true,
    optional: true,
    className: css.columnClass,
    childGenerate: (tags) => <SalesChannelStatusIcon status={tags} />,
    key: 'status',
    width: 88,
  },
  {
    title: () => (
      <div>
        <EditIcon />
        <span>{getI18n('salesChannelContent.index.versionname')}</span>
      </div>
    ),

    childGenerate: (text, record, index) => {
      const { versionName, notesError, publishable } = record;
      return (
        <div className={css.draftName}>
          <TableInput
            disabled={!canEdit}
            maxLength={100}
            onBlurCallback={tableOnBlur}
            error={notesError}
            defaultValue={versionName}
            onChange={(e) => {
              tableOnChange(index, { name: e }, record.uuid);
            }}
          />
          {!publishable && (
            <ExclamationCircleFilled rev className={css.draftNameErr} />
          )}
        </div>
      );
    },
    key: 'versionName',
    minWidth: 264,
    default: true,
  },
  {
    title: `${getI18n('general.created')} (UTC ${timezoneGmt})`,
    key: 'createdDate',
    dataIndex: 'createdDate',
    minWidth: 180,
    default: true,
    sorter: (a, b) => {
      return moment(a.createdDate || null).isBefore(
        moment(b.createdDate || null),
      )
        ? -1
        : 1;
    },
    childGenerate: (text, record, index) =>
      text ? (
        <GlobalTooltip
          title={handleOnFormatCurrentSCTimeAndDateWithTimezone(text)}>
          {handleOnFormatCurrentSCTimeAndDateWithTimezone(text)}
        </GlobalTooltip>
      ) : (
        ''
      ),
  },
  {
    title: `${getI18n('general.modified')} (UTC ${timezoneGmt})`,
    key: 'modifiedDate',
    dataIndex: 'modifiedDate',
    minWidth: 180,
    default: true,
    sorter: (a, b) => {
      return moment(a.modifiedDate || null).isBefore(
        moment(b.modifiedDate || null),
      )
        ? -1
        : 1;
    },
    childGenerate: (text, record, index) =>
      text ? (
        <GlobalTooltip
          title={handleOnFormatCurrentSCTimeAndDateWithTimezone(text)}>
          {handleOnFormatCurrentSCTimeAndDateWithTimezone(text)}
        </GlobalTooltip>
      ) : (
        ''
      ),
  },
  {
    title: getI18n(
      'general.advancedFilters.commonStoreProductsWithTable.actions',
    ),
    key: 'endTime',
    dataIndex: 'endTime',
    minWidth: 378,
    default: true,
    align: 'center',
    childGenerate: (text, record, index) => {
      const {
        uuid = '',
        versionName = '',
        status,
        publishable,
        defaultVersion,
      } = record;
      return (
        <div className={css.menuContain}>
          {overlayActionDraft({
            publishFun: () =>
              publishFun(uuid, getI18n('general.publish'), !publishable),
            duplicateFun: () => duplicateFun(versionName, uuid),
            deleteFun: () =>
              MultiErrorModalFun(
                uuid,
                status,
                getI18n('general.delete'),
                defaultVersion,
              ),
            editFun: () => editFun(uuid),
            editLink: `/salesChannel/editor/content/homepage/${saleChannelUuid}/${uuid}`,
            canView,
            canEdit,
            canPublish,
            isPlanActiveOrPending,
            canDelete,
            getI18n,
          }).map((item) =>
            item.type === ActionType.Button ? (
              item.data && item.data.length ? (
                <DropDown.Button
                  key={item.key}
                  getPopupContainer={() => document.body}
                  overlay={overlay(item.data)}
                  onClick={() => {
                    if (!item.disabled) {
                      item.function();
                    }
                  }}
                  placement='bottomRight'
                  trigger={['click']}>
                  {item.name}
                </DropDown.Button>
              ) : (
                <Button
                  key={item.key}
                  onClick={() => item.function()}
                  disabled={item.disabled}
                  className={css.tableButton}>
                  {item.name}
                </Button>
              )
            ) : (
              <></>
              // <Link
              //   className={css.link}
              //   to={item.to}
              //   key={item.name}
              //   disable={item.disabled}>
              //   {item.name}
              // </Link>
            ),
          )}
        </div>
      );
    },
  },
];

const overlay = (data) => (
  <Menu>
    {data.map((item, index) => (
      <MenuItem
        className={css.menuItem}
        key={item.key}
        data-menu-id={`${item.id || ''}_${index}`}
        disabled={item.disabled}
        onClick={() => item.function()}>
        {item.value}
      </MenuItem>
    ))}
  </Menu>
);

const overlayActionNoDraft = ({
  duplicateFun,
  MultiErrorModalFun,
  scheduleFun,
  editFun,
  editLink,
  canView,
  canEdit,
  canPublish,
  canDelete,
  getI18n,
}) => [
  {
    name: getI18n('salesChannelContent.index.unPublish'),
    type: ActionType.Button,
    key: 1,
    disabled: !canPublish,
    function: () => MultiErrorModalFun('UnPublish'),
    data: [
      {
        key: 1,
        value: getI18n('salesChannelContent.index.schedule'),
        function: () => scheduleFun(),
        disabled: !canPublish,
      },
      {
        key: 2,
        value: getI18n('salesChannelContent.index.unPublish'),
        function: () => MultiErrorModalFun('UnPublish'),
        disabled: !canPublish,
      },
    ],
  },
  {
    name: getI18n('general.tableActions.edit'),
    key: 2,
    type: ActionType.Button,
    function: () => editFun(),
    disabled: !canEdit,
    data: [
      {
        key: 1,
        value: getI18n('general.tableActions.edit'),
        function: () => editFun(),
        disabled: !canEdit,
      },
      {
        key: 2,
        value: getI18n('general.duplicate'),
        function: () => duplicateFun(),
        disabled: !canEdit,
      },
      {
        key: 3,
        value: getI18n('general.delete'),
        function: () => MultiErrorModalFun('Delete'),
        disabled: !canDelete,
      },
    ],
  },
  // {
  //   name: getI18n('salesChannelContent.index.preview'),
  //   key: 3,
  //   type: ActionType.Link,
  //   to: editLink,
  //   disabled: !canView,
  // },
];

const overlayActionDraft = ({
  publishFun,
  duplicateFun,
  deleteFun,
  editFun,
  editLink,
  canView,
  canEdit,
  canPublish,
  isPlanActiveOrPending,
  canDelete,
  getI18n,
}) => [
  {
    name: getI18n('general.publish'),
    type: ActionType.Button,
    key: 1,
    function: () => publishFun(),
    disabled: !canPublish || !isPlanActiveOrPending,
  },
  {
    name: getI18n('general.edit'),
    key: 2,
    type: ActionType.Button,
    function: () => editFun(),
    disabled: !canEdit,
    data: [
      {
        key: 1,
        value: getI18n('general.edit'),

        id: 'sales_channel_list_edit',
        function: () => editFun(),
        disabled: !canEdit,
      },
      {
        key: 2,
        value: getI18n('general.duplicate'),

        id: 'sales_channel_list_duplicate',
        function: () => duplicateFun(),
        disabled: !canEdit,
      },
      {
        key: 3,
        value: getI18n('general.delete'),

        id: 'sales_channel_list_delete',
        function: () => deleteFun(),
        disabled: !canDelete,
      },
    ],
  },
  // {
  //   name: getI18n('salesChannelContent.index.preview'),
  //   key: 3,
  //   type: ActionType.Link,
  //   to: editLink,
  //   disabled: !canView,
  // },
];

export { breadcrumbData, columnsDraft, columnsNoDraft };
